import React, { useEffect, useState } from "react";
import { ShoppingBag, Filter, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const CATS = [
  { key: "", label: "All" },
  { key: "rudraksha", label: "Rudraksha" },
  { key: "yantra", label: "Yantra" },
  { key: "gemstone", label: "Gemstones" },
  { key: "book", label: "Books" },
  { key: "incense", label: "Incense" },
  { key: "samagri", label: "Pooja Samagri" },
  { key: "digital", label: "Digital" },
];

export default function Marketplace() {
  const [cat, setCat] = useState("");
  const [items, setItems] = useState([]);
  const [ordering, setOrdering] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products${cat ? `?category=${cat}` : ""}`).then((r) => setItems(r.data));
  }, [cat]);

  const buy = async (p) => {
    if (!user || user === false) { nav("/login"); return; }
    setOrdering(p.id);
    try {
      await api.post("/orders", { product_id: p.id, quantity: 1 });
      toast.success(`${p.name.slice(0, 30)}… ordered!`);
    } catch (e) {
      toast.error("Could not place order.");
    }
    setOrdering(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="mb-10 grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Sacred Marketplace</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
            Authentic. Energised. <span className="italic gold-gradient">Sourced with reverence.</span>
          </h1>
        </div>
        <div className="md:col-span-4 md:text-right text-[#A0A4AB] text-sm">
          Every product undergoes Prana-Pratishtha ritual before dispatch.
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-8">
        <Filter size={14} className="text-[#D4AF37] mr-1" />
        {CATS.map((c) => (
          <button
            key={c.key || "all"}
            data-testid={`shop-cat-${c.key || "all"}`}
            onClick={() => setCat(c.key)}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition ${
              cat === c.key
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-white/15 text-[#A0A4AB] hover:border-white/30"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((p) => (
          <div key={p.id} data-testid={`product-card-${p.id}`} className="group rounded-xl bg-[#0A0C10] border border-white/10 hover:border-[#D4AF37]/40 transition-colors overflow-hidden">
            <div className="aspect-square overflow-hidden bg-[#12151C]">
              <img src={p.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={p.name} />
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80">{p.category}</div>
              <div className="font-serif text-base text-[#EAE6DF] leading-snug mt-1 line-clamp-2 min-h-[3rem]">{p.name}</div>
              <div className="text-xs text-[#A0A4AB] mt-2 line-clamp-2">{p.description}</div>
              <div className="flex items-center justify-between mt-4">
                <div className="font-serif text-lg text-[#D4AF37]">₹{p.price.toLocaleString("en-IN")}</div>
                <button
                  data-testid={`buy-btn-${p.id}`}
                  onClick={() => buy(p)}
                  disabled={ordering === p.id}
                  className="px-4 py-2 text-xs rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition-colors disabled:opacity-60 inline-flex items-center gap-1"
                >
                  {ordering === p.id ? "Ordering…" : <><ShoppingBag size={12} /> Buy</>}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
